import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { AgentInterceptionType } from '../../reducers/messagingSlice';
import { selectCanPromoteToOrder } from '../../redux/features/agentMode/agentMode.selector';
import { selectDraftCartItems } from '../../redux/features/draftCart/draftCart.selector';
import {
  promoteDraftLine,
  replaceOrderWithDraftCart,
} from '../../redux/features/draftCart/draftCart.thunks';
import useAgentInterception from '../agentInterception/useAgentInterception.hooks';

/**
 * HITL-1615 — promote My Cart into the order.
 *
 * ## Why an interception has to go out, and why it goes FIRST
 *
 * C6's drafted AC assumed `agent_cart` would start flowing on its own "because
 * the mode is `Intercept`". Nothing downstream reads the mode. Three behaviours
 * read `isAIAutoMode`, and all three are still in their AI-driving state while
 * the agent sits in an ARMED `Intercept` — mode chosen, nothing emitted yet:
 *
 * - `useAgentCartSync.hooks.ts:35` returns early, so no `agent_cart` is
 *   published;
 * - `orderSlice.ts:115` returns early, so no `order` reaches the POS;
 * - `useHypothesisFrames.hooks.ts:41` keeps replaying, and its rebuild calls
 *   `clearCart()` (`:254`) — so the next changed frame DESTROYS the promoted
 *   cart.
 *
 * `isAIAutoMode` flips false only on a `sendAgentInterception`, and entering
 * `Intercept` deliberately emits nothing (HITL-1604's armed-versus-engaged
 * design: the choice stays reversible until something leaves the terminal).
 * Promoting is the moment it stops being reversible, so promoting is exactly
 * where the interception belongs.
 *
 * **Order matters.** `useHypothesisFrames` captures its arm baseline on the
 * true→false flip (`:110-116`). Emitting first means the baseline is the AI's
 * cart as it stands; the cart write that follows then reads as the agent's first
 * edit and disarms the intercept-import. Emitting after would bake the promoted
 * items into the baseline and leave the import armed.
 *
 * The emit cannot live in the thunk: it routes through `useAgentInterception`,
 * which is the HITL-1606 gate and is a hook. This hook owns the pair so the
 * ordering exists in one place rather than at each call site.
 */
const usePromoteDraftCart = () => {
  const dispatch = useAppDispatch();
  const { sendAgentInterception } = useAgentInterception();
  const draftCartItems = useAppSelector(selectDraftCartItems);
  // The logic guard behind the Intercept-only controls, same pattern as the rest
  // of the feature. Without it a refused interception would still be followed by
  // the cart write — the agent would silently move their draft into an order
  // that cannot be sent, and the AI's next frame would destroy it.
  const canPromote = useAppSelector(selectCanPromoteToOrder);

  const announce = (description: string) => {
    sendAgentInterception({
      data: {
        type: AgentInterceptionType.ORDER_CHANGED,
        description,
      },
    });
  };

  /** Append one draft line to the order; it leaves My Cart. */
  const promoteLine = (cartItemId: number) => {
    if (!canPromote) return;
    const item = draftCartItems?.[cartItemId];
    if (!item) return;
    announce(`${item.name} added to order from My Cart`);
    dispatch(promoteDraftLine(cartItemId));
  };

  /** Replace the order's items with My Cart's, and empty My Cart. */
  const replaceOrder = () => {
    if (!canPromote) return;
    const count = Object.keys(draftCartItems ?? {}).length;
    if (!count) return;
    announce(`Order replaced with My Cart (${count} line(s))`);
    dispatch(replaceOrderWithDraftCart());
  };

  return { promoteLine, replaceOrder };
};

export default usePromoteDraftCart;
